import { Route, Switch, useLocation } from "wouter";
import { AppShell } from "./app/AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { BillingPage } from "./features/billing/BillingPage";

import { ContainerDetailPage } from "./features/containers/ContainerDetailPage";
import { DevelopSessionPage } from "./features/develop/DevelopSessionPage";
import { EnvironmentDetailPage } from "./features/environments/EnvironmentDetailPage";
import { EnvironmentsListPage } from "./features/environments/EnvironmentsListPage";
import { GalleryPage } from "./features/gallery/GalleryPage";
import { GovernPage } from "./features/govern/GovernPage";

import { HostDetailPage } from "./features/hosts/HostDetailPage";
import { HostsListPage } from "./features/hosts/HostsListPage";
import { OrgImportPage } from "./features/import/OrgImportPage";
import { InstancesListPage } from "./features/instances/InstancesListPage";
import { AgentSessionsPage } from "./features/agent-sessions/AgentSessionsPage";
import { BonnieSessionsPage } from "./features/bonnie/BonnieSessionsPage";
import { OperatePage } from "./features/operate/OperatePage";
import { ProjectDetailPage } from "./features/projects/ProjectDetailPage";
import { ProjectsListPage } from "./features/projects/ProjectsListPage";
import { RoutesListPage } from "./features/routes/RoutesListPage";
import { ServiceDetailPage } from "./features/services/ServiceDetailPage";
import { SettingsPage } from "./features/settings/SettingsPage";
import { LoginPage } from "./routes/LoginPage";
import { RegisterPage } from "./routes/RegisterPage";
import { NotFoundPage } from "./routes/NotFoundPage";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { RoleLanding } from "./routes/RoleLanding";
import { SSOErrorPage } from "./routes/SSOErrorPage";
import { SSORedirectPage } from "./routes/SSORedirectPage";

/**
 * FE-M1: `ProtectedRoute`/`AppShell`/`FleetProvider` are mounted exactly
 * once here, above a single inner `<Switch>` — not per-route as before
 * (every one of the routes below used to nest its own copy). That single
 * outer mount is what keeps the shell's event subscription
 * (`useContainerEventsSubscription`, wired inside `AppShell`) alive across
 * navigation between any two protected pages: React keeps a component
 * instance mounted as long as it stays at the same position in the tree
 * with the same type, and since the outer `<Route>` matches (and its
 * `AppShell` therefore stays rendered) for every protected path, only a
 * round trip through `/login` unmounts it. Once the real per-fleet event
 * channel lands (P-1), this is what stops it from silently dropping events
 * across every navigation.
 */
export function App() {
  const [location] = useLocation();
  return (
    <Switch>
      <Route path="/login">
        <LoginPage />
      </Route>

      <Route path="/register">
        <RegisterPage />
      </Route>

      {/* Public, unauthenticated by definition (ADR-0037 §UX): an
          organization's distributed SSO sign-in link, and the landing spot
          for a refused/failed round trip. Neither belongs behind
          ProtectedRoute — see each component's doc comment. */}
      <Route path="/t/:slug/sso">
        <SSORedirectPage />
      </Route>
      <Route path="/sso/error">
        <SSOErrorPage />
      </Route>

      {/* No `path`: matches every route below (anything other than
          `/login`) so the shell mounts once for the whole authenticated
          app. `ProtectedRoute` still redirects to `/login` reactively if
          there's no session — unauthenticated visitors never see the shell
          around a page, matching the previous per-route behavior. */}
      <Route>
        <ProtectedRoute>
          <AppShell>
            {/* Keyed on the current path: a crash on one page renders the
                fallback in place of that page only (nav chrome above stays
                interactive), and navigating anywhere else remounts a fresh
                boundary rather than getting stuck showing the old page's
                fallback forever. */}
            <ErrorBoundary key={location} label="This page">
              <Switch>
                {/* IA slice 1 (docs/product/ui-information-architecture.md
                    §3): "/" renders Home directly for every fleet role
                    (admin/operator/viewer) — the "what do you want to run?"
                    hero + entry cards ARE the empty-state story now, so
                    there's no data-dependent first-run detour to compute
                    here anymore. A billing_admin (no fleet view to land on)
                    still redirects straight to /billing. See
                    routes/RoleLanding.tsx. */}
                <Route path="/">
                  <RoleLanding />
                </Route>

                {/* Operate (IA slice 7, docs/product/ui-information-architecture.md §3):
                    the App Developer's whole world — services, health, alerts. One page,
                    three tabs that are also real, deep-linkable routes — same "tab and
                    route are the same thing" pattern as GovernPage (IA slice 5). Replaces
                    the prior `/my-services` + `/health` + `/alerts` as independent
                    top-level routes with SectionSubNav navigation between them. Pre-beta
                    clean break — the old paths are retired outright. */}
                <Route path="/operate">
                  <OperatePage />
                </Route>

                <Route path="/operate/health">
                  <OperatePage />
                </Route>

                <Route path="/operate/alerts">
                  <OperatePage />
                </Route>

                {/* IA slice 6 (docs/product/ui-information-architecture.md §5): the
                    fleet-wide containers browser (`ContainersListPage`) is retired as a
                    top-level destination — every capability it carried (adopt, per-
                    container lifecycle, logs/terminal) lives on inside a machine's
                    (Fleet) or a service's (Operate) detail page now, both of which link
                    straight to this one. A container id is only unique per node (see
                    api/client.ts), so the node rides along in the URL too — deep links
                    stay resolvable without first loading a machine or service. */}
                <Route path="/containers/:nodeId/:containerId">
                  <ContainerDetailPage />
                </Route>

                {/* Machines (IA slice 2, docs/product/ui-information-
                    architecture.md §3): the unified Fleet view — ONE object
                    per machine, joining the provisioning facet
                    (ex-Instances), the live-agent facet (ex-Nodes), the
                    environment binding (with "Change environment"), and this
                    machine's containers with lifecycle verbs. Replaces the
                    prior hosts∪nodes split entirely — the pre-unification
                    `/nodes` list/detail routes are retired as of IA slice 6
                    (pre-beta clean break, no alias kept). */}
                <Route path="/hosts">
                  <HostsListPage />
                </Route>

                <Route path="/hosts/:hostId">
                  <HostDetailPage />
                </Route>

                {/* Fleet-wide edge routing overview (ADR-0026 §5) — read-only,
                    composed client-side from projects/services/domains/
                    placements (see RoutesListPage / useRouteRows). */}
                <Route path="/routes">
                  <RoutesListPage />
                </Route>


                {/* Hero flow: project -> service -> deploy (see
                    ServiceDetailPage), plus the Hetzner provisioning half of
                    the flow. Services live nested under their project rather
                    than at their own top-level route — a service id is only
                    meaningful in the context of the project that owns it, same
                    reasoning as containers nesting under nodeId. */}
                <Route path="/projects">
                  <ProjectsListPage />
                </Route>

                {/* Gallery: curated stack recipes (M2 PR-4, #245) — another
                    Develop-arc entry point alongside Projects, deep-linking
                    into the same create-project -> compose-import path (see
                    GalleryPage.tsx). */}
                <Route path="/gallery">
                  <GalleryPage />
                </Route>

                {/* Project detail (IA slice 3, ui-information-architecture.md §3): ONE
                    page, four routes — Overview/Corpus/Sessions/Services are tabs that
                    are also real, deep-linkable routes ("tab and route are the same
                    thing"), all rendering `ProjectDetailPage`, which derives which tab is
                    active from which of these four matched (see its own doc comment).
                    `/projects/:projectId/corpus` predates this slice and keeps working
                    unchanged as a deep link — it just renders the Corpus tab now instead
                    of the old standalone `CorpusPage` route. */}
                <Route path="/projects/:projectId">
                  <ProjectDetailPage />
                </Route>

                <Route path="/projects/:projectId/corpus">
                  <ProjectDetailPage />
                </Route>

                <Route path="/projects/:projectId/sessions">
                  <ProjectDetailPage />
                </Route>

                <Route path="/projects/:projectId/services">
                  <ProjectDetailPage />
                </Route>

                {/* The Develop conversational authoring view (ADR-0052 M6 §2.2):
                    launched from the Sessions tab's (or Corpus tab's) "Develop with an
                    agent" launcher, which already knows both ids — a develop session is
                    only ever reached this way (there is no cross-project develop-sessions
                    list; see DevelopSessionPage's doc comment for why). Breadcrumbs back
                    through the project's Sessions tab, not the old Corpus-only route. */}
                <Route path="/projects/:projectId/develop/:sessionId">
                  <DevelopSessionPage />
                </Route>

                <Route path="/projects/:projectId/services/:serviceId">
                  <ServiceDetailPage />
                </Route>

                {/* First-class environments (ADR-0035 §1) — the policy-bearing
                    grouping of nodes. Create/update/delete are admin-only,
                    gated inside each page (server enforces the real decision,
                    S-8); view is open to every fleet role. */}
                <Route path="/environments">
                  <EnvironmentsListPage />
                </Route>

                <Route path="/environments/:envId">
                  <EnvironmentDetailPage />
                </Route>

                <Route path="/agent-sessions">
                  <AgentSessionsPage />
                </Route>

                <Route path="/bonnie">
                  <BonnieSessionsPage />
                </Route>

                {/* Platform/advanced view (pre-unification): raw provisioning
                    records, reachable from a host's "Advanced" section. */}
                <Route path="/instances">
                  <InstancesListPage />
                </Route>

                {/* "Bring your existing infra" (ADR-0048 §2, #472): read a connected
                    provider credential's existing organization and propose the
                    canonical mapping — infer -> confirm -> apply, nothing created
                    until the operator selects and applies. */}
                <Route path="/import">
                  <OrgImportPage />
                </Route>

                {/* Govern (IA slice 5, docs/product/ui-information-architecture.md §6):
                    "the record" (Audit + Decisions), "the rules" (Policy — read-only
                    aggregate, links to the real editing surfaces), "the custody"
                    (Credentials, see CredentialsHome). One page, four tabs that are also
                    real, deep-linkable routes — same "tab and route are the same thing"
                    pattern as ProjectDetailPage (IA slice 3). IA slice 6 renamed the URL
                    root from `/admin/audit` to `/govern` (founder ruling, slice 5 PR) —
                    a clean rename, not a redirect: pre-beta, no alias is kept (see
                    GovernPage.tsx/NavSidebar.tsx, updated to match). */}
                <Route path="/govern">
                  <GovernPage />
                </Route>

                <Route path="/govern/decisions">
                  <GovernPage />
                </Route>

                <Route path="/govern/policy">
                  <GovernPage />
                </Route>

                <Route path="/govern/credentials">
                  <GovernPage />
                </Route>

                <Route path="/govern/credentials/provider">
                  <GovernPage />
                </Route>

                <Route path="/govern/credentials/llm">
                  <GovernPage />
                </Route>

                {/* Settings (IA slice 5): users · SSO · billing · account, one page with
                    role-aware tabs (hidden not greyed — S-8). Account is reachable by every
                    signed-in role (linked from TopBar, not NavSidebar) — that's this base
                    route's default tab. IA slice 6 renamed the URL root from `/account` to
                    `/settings` (founder ruling, slice 5 PR) — a clean rename, not a
                    redirect (see SettingsPage.tsx/NavSidebar.tsx/TopBar.tsx, updated to
                    match). */}
                <Route path="/settings">
                  <SettingsPage />
                </Route>

                <Route path="/settings/users">
                  <SettingsPage />
                </Route>

                <Route path="/settings/sso">
                  <SettingsPage />
                </Route>

                <Route path="/settings/billing">
                  <SettingsPage />
                </Route>

                {/* Billing (L-1). Reachable by admin and billing_admin; the page itself
                    shows a "no access" state for anyone else, and the server enforces the
                    real decision (S-8). Deliberately UNTOUCHED by the Settings
                    consolidation above: `billing_admin`'s landing path (`roleView.ts`'s
                    `landingPathFor`) points straight here, and that must keep rendering
                    BillingPage directly rather than redirecting into a Settings shell that
                    role never otherwise sees (it holds no fleet grants — `canSeeFleet` is
                    false for it, so it never reaches the six-destination nav Settings'
                    tabs live inside). Settings' own Billing tab (`/settings/billing`, admin
                    and billing_admin) mounts this exact same component — not a second
                    implementation (A-5). */}
                <Route path="/billing">
                  <BillingPage />
                </Route>

                {/* No `path`: matches whatever falls through the routes above,
                    same as react-router's catch-all `*`. */}
                <Route>
                  <NotFoundPage />
                </Route>
              </Switch>
            </ErrorBoundary>
          </AppShell>
        </ProtectedRoute>
      </Route>
    </Switch>
  );
}
